import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Decimal,
  etatRealisation,
  formaterDate,
  formaterDateHeure,
  formaterIdentite,
  formaterMontantDevise,
  formaterParts,
  formaterPourcentage,
  formaterPrix,
  formaterQuantite,
  montant,
  somme,
} from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur, useRessource } from '../api/ressource';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Nombre } from '../composants/Nombre';
import { Pastille } from '../composants/Pastille';
import { statutOrdre } from '../composants/statutOrdre';
import { LienFiche } from '../composants/LienAction';
import { LIBELLE_PANIER, TON_PANIER, panierTransmissible } from '../composants/statutPanier';

interface OrdreDuPanier {
  id: string;
  reference: string;
  sens: string;
  mode: string;
  statut: string;
  quantiteDemandee: string | null;
  montantDemande: string | null;
  montantEstime: string;
  coursIndicatif: string | null;
  quantiteRealisee: string | null;
  montantRealise: string | null;
  prixDenouement: string | null;
  mandat: { id: string; reference: string; client: { nom: string; prenom: string } };
  instrument: { libelle: string; isin: string | null } | null;
  tranche: { libelle: string } | null;
}

interface Panier {
  id: string;
  reference: string;
  canal: string;
  datePanier: string;
  statut: string;
  transmisLe: string | null;
  clotureLe: string | null;
  cloturePar: string | null;
  ordres: OrdreDuPanier[];
}

const LIBELLE_CANAL: Record<string, string> = {
  BOURSE: 'Société de bourse — actions',
  SALLE_MARCHES: 'Salle des marchés — BDT',
  OPCVM: 'Sociétés de gestion — OPCVM',
};

/** Un ordre en montant se lit en MAD, un ordre en quantité en titres. */
function demande(o: OrdreDuPanier): string {
  return o.mode === 'QUANTITE' && o.quantiteDemandee
    ? `${formaterQuantite(o.quantiteDemandee)} titres`
    : formaterMontantDevise(o.montantDemande ?? o.montantEstime);
}

/** Parts d'OPCVM à 4 décimales, titres entiers partout ailleurs. */
function realise(o: OrdreDuPanier, canal: string): string {
  if (o.quantiteRealisee === null) return '—';
  return canal === 'OPCVM'
    ? `${formaterParts(o.quantiteRealisee)} parts`
    : `${formaterQuantite(o.quantiteRealisee)} titres`;
}

/** Un BDT se cote en pourcentage du nominal, jamais en prix unitaire. */
function prix(valeur: string | null, canal: string): string {
  return canal === 'SALLE_MARCHES' ? formaterPourcentage(valeur, 3) : formaterPrix(valeur);
}

export function FichePanier() {
  const { id } = useParams<{ id: string }>();
  const { aRole } = useAuth();
  const { donnees, chargement, erreur, recharger } = useRessource<Panier>(`/paniers/${id}`);
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const peutGerer = aRole('MIDDLE_BO', 'RESP_GESTION');
  const canal = donnees?.canal ?? '';

  async function rejeter(o: OrdreDuPanier) {
    const motif = window.prompt(`Motif de la non-exécution de ${o.reference}`);
    if (!motif) return;
    setErreurAction(null);
    setOccupe(true);
    try {
      await api.post('/ordres/rejeter', { ids: [o.id], motif });
      await recharger();
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Opération impossible.');
    } finally {
      setOccupe(false);
    }
  }

  async function cloturer(reliquats: number) {
    const avertissement =
      reliquats > 0
        ? `${reliquats} ordre(s) n’ont pas été servis en totalité. Une fois le panier clôturé, leurs reliquats ne seront plus rapprochés avec les situations suivantes. Clôturer ?`
        : 'Clôturer ce panier ?';
    if (!window.confirm(avertissement)) return;
    setErreurAction(null);
    setOccupe(true);
    try {
      await api.post(`/paniers/${id}/cloturer`);
      await recharger();
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Opération impossible.');
    } finally {
      setOccupe(false);
    }
  }

  async function transmettre() {
    setErreurAction(null);
    setOccupe(true);
    try {
      await api.post(`/paniers/${id}/transmettre`);
      await recharger();
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Opération impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<OrdreDuPanier>[] = [
    { cle: 'reference', entete: 'Ordre', num: true, rendu: (o) => o.reference },
    {
      cle: 'mandat',
      entete: 'Mandat',
      rendu: (o) => <LienFiche to={`/mandats/${o.mandat.id}`}>{o.mandat.reference}</LienFiche>,
    },
    {
      cle: 'client',
      entete: 'Client',
      rendu: (o) => formaterIdentite(o.mandat.client.nom, o.mandat.client.prenom),
    },
    {
      cle: 'sens',
      entete: 'Sens',
      rendu: (o) => (
        <span className={o.sens === 'ACHAT' ? 'ordre-achat' : 'ordre-vente'}>{o.sens}</span>
      ),
    },
    {
      cle: 'sousjacent',
      entete: 'Sous-jacent',
      // Un achat BDT ne porte pas de ligne : elle est choisie par la salle
      // des marchés et découverte à l'exécution (RG-17).
      rendu: (o) => o.instrument?.libelle ?? `BDT · ${o.tranche?.libelle ?? '—'}`,
    },
    { cle: 'isin', entete: 'ISIN', num: true, rendu: (o) => o.instrument?.isin ?? '—' },
    { cle: 'demande', entete: 'Demandé', num: true, rendu: demande },
    {
      cle: 'prixTransmis',
      entete: 'Prix transmis',
      num: true,
      // Seul un ordre en quantité part avec un cours ; un ordre en montant
      // laisse le prix au partenaire.
      rendu: (o) => <span className="n">{prix(o.coursIndicatif, canal)}</span>,
    },
    {
      cle: 'estime',
      entete: 'Montant estimé',
      num: true,
      rendu: (o) => <Nombre valeur={o.montantEstime} />,
    },
    { cle: 'realise', entete: 'Réalisé', num: true, rendu: (o) => realise(o, canal) },
    {
      cle: 'prixDenouement',
      entete: 'Prix de dénouement',
      num: true,
      rendu: (o) => <span className="n">{prix(o.prixDenouement, canal)}</span>,
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (o) => {
        const { libelle, ton } = statutOrdre(o);
        return <Pastille ton={ton}>{libelle}</Pastille>;
      },
    },
    {
      cle: 'action',
      entete: '',
      num: true,
      // L'ordre est parti : on ne l'annule pas, on constate qu'il n'a pas
      // été servi — ou que le solde ne viendra plus.
      rendu: (o) =>
        peutGerer && o.statut === 'TRANSMIS' ? (
          <button
            type="button"
            className="bouton bouton--compact bouton--fantome"
            disabled={occupe}
            onClick={() => void rejeter(o)}
          >
            Non exécuté
          </button>
        ) : (
          ''
        ),
    },
  ];

  if (chargement) return <p className="surtitre">Chargement…</p>;
  if (erreur || !donnees)
    return (
      <BandeauAlerte nature="refus" titre="Panier introuvable">
        {messageErreur(erreur) ?? 'Ce panier n’existe pas.'}
      </BandeauAlerte>
    );

  const p = donnees;
  const totalEstime = somme(p.ordres.map((o) => montant(o.montantEstime)));
  const denoues = p.ordres.filter((o) => o.statut === 'DENOUE' || o.statut === 'REJETE').length;
  const attendus = p.ordres.filter((o) => o.statut === 'TRANSMIS').length;
  const mesures = p.ordres.filter((o) => o.montantRealise !== null);
  const totalRealise = mesures.reduce((s, o) => s.plus(o.montantRealise!), new Decimal(0));
  const partiels = p.ordres.filter((o) => {
    const etat = etatRealisation(o);
    return etat === 'PARTIEL' || etat === 'NON_SERVI';
  }).length;

  return (
    <>
      <p className="entete__fil">
        <Link to="/paniers">Paniers</Link> › {p.reference}
      </p>
      <div className="entete">
        <h1>{LIBELLE_CANAL[p.canal] ?? p.canal}</h1>
        <span className="entete__ref">{p.reference}</span>
        <Pastille ton={TON_PANIER[p.statut] ?? 'neutre'}>
          {LIBELLE_PANIER[p.statut] ?? p.statut}
        </Pastille>
      </div>

      <section className="cles" aria-label="Chiffres clés">
        <div className="cle">
          <span className="surtitre">Ordres</span>
          <div className="cle__valeur">{p.ordres.length}</div>
          <div className="appoint">
            {denoues} dénoué(s) · {attendus} en attente
          </div>
        </div>
        <div className="cle">
          <span className="surtitre">Montant estimé</span>
          <div className="cle__valeur">
            <Nombre valeur={totalEstime} />
            <small>MAD</small>
          </div>
        </div>
        <div className="cle">
          <span className="surtitre">Montant réalisé</span>
          <div className="cle__valeur">
            {mesures.length > 0 ? (
              <>
                <Nombre valeur={totalRealise} />
                <small>MAD</small>
              </>
            ) : (
              '—'
            )}
          </div>
          <div className="appoint">{partiels} ordre(s) servi(s) en partie ou pas du tout</div>
        </div>
        <div className="cle">
          <span className="surtitre">Date du panier</span>
          <div className="cle__valeur">{formaterDate(p.datePanier)}</div>
        </div>
        <div className="cle">
          <span className="surtitre">Transmis le</span>
          <div className="cle__valeur">{formaterDateHeure(p.transmisLe)}</div>
          {p.clotureLe && (
            <div className="appoint">
              Clôturé le {formaterDateHeure(p.clotureLe)}
              {p.cloturePar ? ' par le back-office' : ', tous ordres servis'}
            </div>
          )}
        </div>
      </section>

      {erreurAction && (
        <BandeauAlerte nature="refus" titre="Opération refusée">
          {erreurAction}
        </BandeauAlerte>
      )}

      {attendus > 0 && p.statut === 'TRANSMIS' && (
        <BandeauAlerte nature="gestion" titre="Retours attendus">
          {attendus} ordre(s) sont chez le partenaire, en attente de dénouement. L’application ne
          reçoit plus d’avis d’exécution : ils se dénoueront à la première situation datée après
          leur transmission, quel qu’ait été leur sort. D’ici là, ils engagent la trésorerie.
        </BandeauAlerte>
      )}

      {p.statut === 'TRANSMIS' && attendus === 0 && partiels > 0 && (
        <BandeauAlerte nature="gestion" titre="Exécution partielle — panier ouvert">
          {partiels} ordre(s) n’ont pas été servis en totalité. Le panier reste ouvert : leurs
          reliquats seront rapprochés avec la prochaine situation reçue, et le panier se clôturera
          seul quand tout sera servi. Si le reliquat ne viendra pas, clôturez-le : le gérant le
          reprendra par de nouveaux ordres.
        </BandeauAlerte>
      )}

      <Bloc titre="Ordres du panier" meta={`${p.ordres.length} ligne(s)`}>
        <Tableau
          colonnes={colonnes}
          lignes={p.ordres}
          cle={(o) => o.id}
          vide="Aucun ordre dans ce panier."
        />
        <p className="consigne consigne--pied">
          Le réalisé se constate à la situation qui dénoue l’ordre, par écart avec la situation
          précédente : l’application ne reçoit pas d’avis d’opéré. Le prix de dénouement est le prix
          de revient déclaré par le back-office, courtage inclus ; une vente n’en a un que si le
          journal du tiers désigne la ligne cédée.
        </p>
      </Bloc>

      <Bloc titre="Actions">
        <div className="barre-actions">
          <a className="bouton" href={`/api/v1/paniers/${p.id}/export`}>
            Exporter le fichier
          </a>
          {peutGerer && p.statut === 'TRANSMIS' && attendus === 0 && (
            <button
              type="button"
              className="bouton bouton--fantome"
              disabled={occupe}
              onClick={() => void cloturer(partiels)}
            >
              Clôturer le panier
            </button>
          )}
          {peutGerer && panierTransmissible(p.statut) && (
            <button
              type="button"
              className="bouton bouton--fantome"
              disabled={occupe}
              onClick={() => void transmettre()}
            >
              {occupe ? 'Enregistrement…' : 'Marquer transmis'}
            </button>
          )}
        </div>
        <p className="consigne consigne--pied">
          L’envoi au partenaire se fait hors application. Une fois le panier marqué transmis, il
          devient immuable et ses ordres passent à « Transmis » (RG-11). Il se clôture seul quand
          une situation montre tous ses ordres servis en totalité.
        </p>
      </Bloc>
    </>
  );
}
